import {
  React,
  SunMedium,
  Moon,
  useLocation,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Typography,
  Box,
} from '../../utils/globalImports';
import { SettingsButton } from '../SettingsButton/SettingsButton';
import { settingsManager } from '../../utils/settingsManager';
import { RootState } from '../../store';
import { ExtendedAccountResponse } from '../../store/types';
import { useGetClientsByCustomerIdQuery } from '../../api/clientApiSlice';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedClient, clearSelectedClient } from '../../store/slices/client';
import { HelpCallout } from '../HelpCallout/HelpCallout';
import NotificationBell from '../Notifications/NotificationBell';
import { Building2 } from 'lucide-react';

interface Client {
  client_id: string;
  name: string;
}

interface CustomTopbarProps {
  isVaultiscanCollapsed?: boolean;
}

const CustomTopbar: React.FC<CustomTopbarProps> = ({ isVaultiscanCollapsed = false }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const selectedClient = useSelector((state: RootState) => state.client.selectedClient);
  const isChatRoute = location.pathname.startsWith('/chat');
  const auth = useSelector((state: RootState) => state.auth);
  const account = auth.account as ExtendedAccountResponse | null;
  const role = account?.role;
  const customerId = account?.customer_id;
  const { data: clients = [], isLoading } = useGetClientsByCustomerIdQuery(customerId, {
    skip: !customerId || role !== 'CustomerAdmin',
  });

  const handleClientChange = (e: SelectChangeEvent<string>) => {
    const selectedValue = e.target.value;

    if (selectedValue === 'all') {
      dispatch(clearSelectedClient());
    } else {
      const clientObj = clients.find((c: Client) => c.client_id === selectedValue);
      if (clientObj) dispatch(setSelectedClient(clientObj));
    }
  };

  return (
    <div className="custom-topbar">
      <div className="custom-topbar-left">
        {isChatRoute && role === 'CustomerAdmin' && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              ml: isVaultiscanCollapsed ? -35 : -6,
              transition: 'margin-left 0.3s',
            }}
          >
            {/* Workspace label */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Building2 size={14} color="#695ea8" />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: '#695ea8',
                  fontSize: '0.7rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Workspace
              </Typography>
            </Box>

            {/* Styled select */}
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <Select
                value={selectedClient?.client_id ?? 'all'}
                onChange={handleClientChange}
                disabled={isLoading}
                displayEmpty
                sx={{
                  height: 32,
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  color: selectedClient ? '#0F172A' : '#64748b',
                  borderRadius: 1.5,
                  bgcolor: selectedClient ? 'rgba(105,94,168,0.06)' : 'transparent',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: selectedClient ? 'rgba(105,94,168,0.4)' : 'rgba(0,0,0,0.18)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#695ea8',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#695ea8',
                    borderWidth: 1,
                  },
                  '& .MuiSelect-icon': { color: '#695ea8' },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      mt: 0.5,
                      borderRadius: 1.5,
                      border: '1px solid rgba(105,94,168,0.15)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                      '& .MuiMenuItem-root': {
                        fontSize: '0.82rem',
                        fontWeight: 500,
                        borderRadius: 1,
                        mx: 0.5,
                        my: 0.25,
                        '&:hover': { bgcolor: 'rgba(105,94,168,0.08)' },
                        '&.Mui-selected': {
                          bgcolor: 'rgba(105,94,168,0.12)',
                          color: '#695ea8',
                          '&:hover': { bgcolor: 'rgba(105,94,168,0.18)' },
                        },
                      },
                    },
                  },
                }}
              >
                <MenuItem value="all">All Workspaces</MenuItem>
                {clients.length > 0 ? (
                  clients.map((client: Client) => (
                    <MenuItem key={client.client_id} value={client.client_id}>
                      {client.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No workspace found</MenuItem>
                )}
              </Select>
            </FormControl>

            <HelpCallout
              label={undefined}
              labelId="workspace-help"
              fieldId="workspace-select"
              helpText="Select a workspace to filter categories specific to that workspace."
              style={{ marginBottom: '0.1rem' }}
            />
          </Box>
        )}
      </div>

      <div className="custom-topbar-right">
        <NotificationBell />
        <SunMedium className="topbar-icon-sun" />
        <Moon className="topbar-icon-moon" />
        {isChatRoute && <SettingsButton className="topbar-settings-button" onClick={settingsManager.toggle} />}
      </div>
    </div>
  );
};

export default CustomTopbar;
